import { ValidationError } from "sequelize"
import {
    SilDeviceCreate,
    SilDeviceChangeStatus,
    SilDeviceUpdate,

    SilProjectDevice,
    SilProjectDeviceCreate,
    SilProjectDeviceChangeStatus,
    SilProjectDeviceUpdate,
} from "server/models"
import {
    PUBLISHED, UNPUBLISHED,
    NONE, VALUE, UNLIMITED,
    PROVEN_NO, PROVEN_IN_USE, PRIOR_USE,
    LOW_DEMAND_MODE, HIGH_DEMAND_MODE, CONTINUOUS_MODE,
    TYPE_B,
} from "const"
import {
    OrderType,
    ProvenType,
    PublishedUnpublished,
    SilDeviceFilter,
    SilMode,
    SilType,
} from "client/utils"
import {
    GET, POST, DELETE, sort, search, validate,
} from "client/store/utils"
import {
    PackageSettings,
    UserData,
    LangType,
    LoadSilNestedDevicesByFilter,
    LoadSilProjectNestedDevicesByFilter,
} from "client/store"
import {
    SilDeviceType,
    SilDeviceList,
    SilDeviceValidation,
    SilDeviceFilters,
    SilDeviceNestedType,
} from "../types"

export const LoadAllSilDevices = (
    accountId: number,
    projectId: number,
    nested: boolean,
    publishedOnly?: boolean,
) => (
    GET(
        projectId
            ? `/silProjectDevice/all/projectId/${projectId}/nested/${nested ? "true" : "false"}`
            : `/silDevice/all/accountId/${accountId}/nested/${nested ? "true" : "false"}`,
    ).then((devices: SilDeviceType[]) => {
        const deviceList: SilDeviceList = {}
        devices.forEach((device) => {
            if (publishedOnly === true) {
                if (device.status === PUBLISHED) deviceList[device.id] = device
            } else {
                deviceList[device.id] = device
            }
        })
        return deviceList
    })
)

export const LoadSilDeviceById = (
    deviceId: number,
    accountId: number,
    projectId: number,
) => (
    GET(
        projectId
            ? `/silProjectDevice/id/${deviceId}/projectId/${projectId}`
            : `/silDevice/id/${deviceId}/accountId/${accountId}`,
    ).then((device: SilDeviceType) => device)
)

export const LoadUpdatedSilProjectDevices = (projectId: number) => (
    GET(`/silProjectDevice/updated/projectId/${projectId}`)
    .then((deviceCount: number) => deviceCount)
)

export const LoadSilProjectDevicesByFilter = (filters: SilDeviceFilter) => (
    POST(
        "/silProjectDevice/getByFilter",
        JSON.stringify(filters),
    ).then((devices: SilProjectDevice[]) => devices)
)

export const SaveDevice = (
    id: number,
    device: SilDeviceCreate | SilProjectDeviceCreate,
) => {
    const projectDevice = device as SilProjectDeviceCreate
    return POST(
        projectDevice.projectId
            ? "/silProjectDevice/save"
            : "/silDevice/save",
        JSON.stringify({ id, device }),
    )
}

export const SaveSilDevice = (
    id: number,
    device: SilDeviceCreate | SilProjectDeviceCreate,
    maxNumber: number,
    lang: LangType,
) => {
    const accountDevice = device as SilDeviceCreate
    const projectDevice = device as SilProjectDeviceCreate
    return LoadAllSilDevices(
        accountDevice.accountId || null,
        projectDevice.projectId || null,
        device.nested,
    ).then((res1) => {
        const devicesNumber = Object.keys(res1).length

        // unlimited number on project level
        if (!id && !projectDevice.projectId && maxNumber !== null && devicesNumber >= maxNumber) {
            const validationText = device.nested ? lang.maxAccountSubsystemsReached : lang.maxAccountDevicesReached
            return {
                newDeviceId: null,
                validation: <SilDeviceValidation>{
                    name: validationText.replace("{0}", maxNumber.toString()),
                },
            }
        }

        return SaveDevice(id, device).then((res) => {
            const newDevice = res as SilDeviceType
            const error = res as ValidationError
            return {
                newDeviceId: newDevice.id || null,
                validation: <SilDeviceValidation>validate(error, lang),
            }
        })
    })
}

export const ChangeSilDeviceStatus = (device: SilDeviceType) => {
    const status: PublishedUnpublished = device.status === PUBLISHED ? UNPUBLISHED : PUBLISHED
    const data: SilDeviceChangeStatus | SilProjectDeviceChangeStatus = {
        id: device.id,
        status,
    }
    const projectDevice = device as SilProjectDevice
    return POST(
        projectDevice.projectId
            ? "/silProjectDevice/changeStatus"
            : "/silDevice/changeStatus",
        JSON.stringify(data),
    ).then((updatedRows: [number]) => (
        updatedRows && updatedRows.length && updatedRows[0] === 1
            ? status
            : device.status
    ))
}

export const SilProjectDeviceUpdateByFilters = (filters: SilDeviceFilter) => (
    POST(
        "/silProjectDevice/updateByFilters",
        JSON.stringify(filters),
    ).then((updatedRows: [number]) => updatedRows[0])
)

export const SilProjectDeviceClearUpdateByFilters = (filters: SilDeviceFilter) => (
    POST(
        "/silProjectDevice/clearUpdateByFilters",
        JSON.stringify(filters),
    ).then((updatedRows: [number]) => updatedRows[0])
)

export const SilDeviceUpdateRequired = (filters: SilDeviceFilter, update = true) => (
    LoadSilNestedDevicesByFilter(filters)
    .then((devices) => {
        const parentDeviceIds: number[] = []
        devices.forEach((device) => {
            parentDeviceIds.push(device.parentDeviceId)
        })

        const data: SilDeviceUpdate = {
            deviceIds: parentDeviceIds,
            update,
        }

        return POST(
            "/silDevice/updateRequired",
            JSON.stringify(data),
        ).then((updatedRows: [number]) => updatedRows[0])
    })
)

export const SilProjectDeviceUpdateRequired = (
    filters: SilDeviceFilter,
    update = true,
) => (
    LoadSilProjectNestedDevicesByFilter(filters)
    .then((devices) => {
        const parentDeviceIds: number[] = []
        devices.forEach((device) => {
            parentDeviceIds.push(device.parentDeviceId)
        })

        const data: SilProjectDeviceUpdate = {
            deviceIds: parentDeviceIds,
            update,
        }

        return POST(
            "/silProjectDevice/updateRequired",
            JSON.stringify(data),
        ).then((updatedRows: [number]) => updatedRows[0])
    })
)

export const DeleteSilDevice = (
    deviceId: number,
    accountId: number,
    projectId: number,
) => (
    DELETE(
        projectId
            ? `/silProjectDevice/delete/${deviceId}/${projectId}`
            : `/silDevice/delete/${deviceId}/${accountId}`,
    ).then((removedRows: number) => (
        projectId
            ? removedRows
            // when remove account device - clear updates for project devices that were copied from account
            : SilProjectDeviceClearUpdateByFilters({ deviceId }).then(() => removedRows)
    ))
)


export const SortSilDevices = (
    devices: SilDeviceType[],
    sortColumn: keyof SilDeviceType,
    sortOrder: OrderType,
    filters: SilDeviceFilters,
): number[] => {
    const devicesFiltered = devices.filter((device) => {
        let showRow = true
        if (filters.manufacturer && device.manufacturer.toUpperCase().indexOf(filters.manufacturer.toUpperCase()) < 0) showRow = false
        else if (filters.name && device.name.toUpperCase().indexOf(filters.name.toUpperCase()) < 0) showRow = false
        else if (filters.type && device.type !== filters.type) showRow = false

        else if (filters.lowDemandMode && device.lowDemandMode !== filters.lowDemandMode) showRow = false
        else if (filters.highDemandMode && device.highDemandMode !== filters.highDemandMode) showRow = false
        else if (filters.continuousMode && device.continuousMode !== filters.continuousMode) showRow = false

        else if (
            (filters.logicSolver === false || filters.logicSolver === true)
            && device.logicSolver !== filters.logicSolver
        ) showRow = false
        else if (
            [PROVEN_NO, PROVEN_IN_USE, PRIOR_USE].indexOf(filters.provenInUse) >= 0
            && device.provenInUse !== filters.provenInUse
        ) showRow = false

        else if (filters.status && device.status !== filters.status) showRow = false
        else if (filters.search) showRow = search(filters.search, device)
        return showRow
    })

    const deviceIds: number[] = []
    devicesFiltered.sort((a, b) => sort(a, b, sortColumn, sortOrder))
    devicesFiltered.forEach((device) => deviceIds.push(device.id))

    return deviceIds
}


export const GetMaxSilDevices = (
    packageSettings: PackageSettings,
    userData: UserData,
    devicesNumber?: number,
    lang?: LangType,
) => {
    const { silAccountDevices, silAccountDevicesValue } = packageSettings

    let maxDevices = 0
    if (silAccountDevices === NONE) maxDevices = 0
    if (silAccountDevices === VALUE && silAccountDevicesValue) maxDevices = silAccountDevicesValue
    if (silAccountDevices === UNLIMITED) maxDevices = null
    if (userData.accountId === 1) maxDevices = null

    // feedbacks
    let feedback: string = ""
    if (lang) {
        if (maxDevices === null) {
            feedback = lang.maxAccountDevicesUnlimited
        } else {
            if (devicesNumber < maxDevices) feedback = lang.maxAccountDevices
            if (devicesNumber === maxDevices) feedback = lang.maxAccountDevicesReached
            if (devicesNumber > maxDevices) feedback = lang.maxAccountDevicesExceeded
            feedback = feedback.replace("{0}", maxDevices.toString())
        }
    }

    return {
        maxDevices,
        deviceFeedback: feedback,
    }
}


export const GetMaxSilSubsystems = (
    packageSettings: PackageSettings,
    userData: UserData,
    subsystemsNumber?: number,
    lang?: LangType,
) => {
    const { silAccountSubsystems, silAccountSubsystemsValue } = packageSettings

    let maxSubsystems = 0
    if (silAccountSubsystems === NONE) maxSubsystems = 0
    if (silAccountSubsystems === VALUE && silAccountSubsystemsValue) maxSubsystems = silAccountSubsystemsValue
    if (silAccountSubsystems === UNLIMITED) maxSubsystems = null
    if (userData.accountId === 1) maxSubsystems = null

    // feedbacks
    let feedback: string = ""
    if (lang) {
        if (maxSubsystems === null) {
            feedback = lang.maxAccountSubsystemsUnlimited
        } else {
            if (subsystemsNumber < maxSubsystems) feedback = lang.maxAccountSubsystems
            if (subsystemsNumber === maxSubsystems) feedback = lang.maxAccountSubsystemsReached
            if (subsystemsNumber > maxSubsystems) feedback = lang.maxAccountSubsystemsExceeded
            feedback = feedback.replace("{0}", maxSubsystems.toString())
        }
    }

    return {
        maxSubsystems,
        subsystemFeedback: feedback,
    }
}


export const CalculateSubsystem = (
    originSubsystem: SilDeviceType,
    nestedDevices: readonly SilDeviceNestedType[],
) => {
    // calculated subsystem data
    let subsystemType: SilType = null
    let subsystemMode: SilMode = null
    let subsystemLogicSolver: boolean = null
    let subsystemProvenInUse: ProvenType = null
    let subsystemSd: number = null
    let subsystemSu: number = null
    let subsystemDd: number = null
    let subsystemDu: number = null

    let disableSave = false
    let emptyBlocks = false
    let counter = 0

    nestedDevices.forEach((device) => {
        if (device === null) {
            disableSave = true
            emptyBlocks = true
            return
        }
        counter += 1

        if (counter === 1) {
            subsystemType = device.type
            subsystemMode = device.mode
            subsystemLogicSolver = device.logicSolver
            subsystemProvenInUse = device.provenInUse
            if (device.sd !== null) subsystemSd = Number(device.sd)
            if (device.su !== null) subsystemSu = Number(device.su)
            if (device.dd !== null) subsystemDd = Number(device.dd)
            if (device.du !== null) subsystemDu = Number(device.du)
        } else {
            // type B always wins over type A
            if (device.type === TYPE_B) subsystemType = TYPE_B
            if (subsystemMode !== device.mode) {
                subsystemMode = null // modes are not the same
                disableSave = true
            }
            if (subsystemLogicSolver !== device.logicSolver) {
                subsystemLogicSolver = null // not all devices logic solvers
                disableSave = true
            }
            if (subsystemProvenInUse !== device.provenInUse) {
                subsystemProvenInUse = null // not all devices 'proven in use'
                disableSave = true
            }
            if (device.sd !== null) {
                if (subsystemSd === null) subsystemSd = Number(device.sd)
                else subsystemSd += Number(device.sd)
            }
            if (device.su !== null) {
                if (subsystemSu === null) subsystemSu = Number(device.su)
                else subsystemSu += Number(device.su)
            }
            if (device.dd !== null) {
                if (subsystemDd === null) subsystemDd = Number(device.dd)
                else subsystemDd += Number(device.dd)
            }
            if (device.du !== null) {
                if (subsystemDu === null) subsystemDu = Number(device.du)
                else subsystemDu += Number(device.du)
            }
        }
    })

    const calculatedSubsystem: SilDeviceType = {
        ...originSubsystem,
        type: subsystemType,
        lowDemandMode: subsystemMode === LOW_DEMAND_MODE,
        highDemandMode: subsystemMode === HIGH_DEMAND_MODE,
        continuousMode: subsystemMode === CONTINUOUS_MODE,
        logicSolver: subsystemLogicSolver,
        provenInUse: subsystemProvenInUse,
        sd: subsystemSd === null ? null : subsystemSd.toString(),
        su: subsystemSu === null ? null : subsystemSu.toString(),
        dd: subsystemDd === null ? null : subsystemDd.toString(),
        du: subsystemDu === null ? null : subsystemDu.toString(),
    }

    return {
        calculatedSubsystem,
        emptyBlocks,
        disableSave,
    }
}
